import React, { memo, useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';
import { UploadsWrapper, UploadsTable, UploadsHeader } from 'Components/Uploads';
import { Spinner } from 'Components/Spinner';

import { projectIdSelector } from 'Containers/RocketScan/selectors';
import { projectPDFsSelector } from 'Containers/Uploads/selectors';
import { listProjectPDFs } from 'Containers/Uploads/actions';
import { UploadFileModal, DeleteFileModal } from 'Containers/Uploads';
import { DashboardPagination } from 'Components/Pagination';
import { download } from 'Utils/helpers';

function UploadsContainer() {
  const dispatch = useDispatch();

  const mounted = useRef(true);

  const projectId = useSelector(projectIdSelector, areEqual);
  const pdfs = useSelector(projectPDFsSelector, areEqual);

  const [sortBy, setSortBy] = useState('-created_at');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPDF, setSelectedPDF] = useState(undefined);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewPdfUrl, setViewPdfUrl] = useState('');
  const [viewPdfName, setViewPdfName] = useState('');
  const [pageCount, setPageCount] = useState(1);
  const [initialPage, setInitialPage] = useState(0);

  const onClickSort = useCallback((sort: string) => {
    setSortBy((prevSortBy) => (prevSortBy.includes('-') ? prevSortBy.replace('-', '') : sort));
  }, []);

  const onUploadButtonClick = useCallback(() => {
    setIsUploadModalOpen(true);
  }, []);

  const onViewIconClick = useCallback(async (pdf: any) => {
    setViewPdfName(pdf.name);
    setIsViewModalOpen(true);
    try {
      const res = await fetch(pdf.pdf_url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      setViewPdfUrl(blobUrl);
    } catch {
      setViewPdfUrl(pdf.pdf_url);
    }
  }, []);

  const closeViewModal = useCallback(() => {
    if (viewPdfUrl.startsWith('blob:')) URL.revokeObjectURL(viewPdfUrl);
    setViewPdfUrl('');
    setIsViewModalOpen(false);
  }, [viewPdfUrl]);

  const onDownloadIconClick = useCallback(async (pdf: any) => {
    const { name, pdf_url: url } = pdf;
    await download(url, name);
  }, []);

  const onDeleteIconClick = useCallback(async (pdf: any) => {
    setSelectedPDF(pdf);
    setIsDeleteModalOpen(true);
  }, []);

  const getProjectPDFs = useCallback(async () => {
    if (projectId) {
      dispatch(listProjectPDFs(projectId, sortBy, initialPage + 1));
    }
  }, [projectId, sortBy, initialPage]);

  const onDeleteSuccess = useCallback(() => {
    setIsDeleteModalOpen(false);
    setSelectedPDF(undefined);
    getProjectPDFs();
  }, [getProjectPDFs]);

  // setup pagination
  useEffect(() => {
    if (pdfs?.data?.length > 0) {
      const { meta } = pdfs;
      const { total } = meta;

      setPageCount(total <= 15 ? 1 : Number(total / 15));
    }
  }, [pdfs]);

  const onPageChange = useCallback(({ selected: page }: any) => {
    setInitialPage(page);
  }, []);

  useEffect(() => {
    mounted.current = true;
    (async function fetchData() {
      await getProjectPDFs();
    })();
    return () => {
      if (mounted) {
        mounted.current = false;
      }
    };
  }, [projectId, sortBy, initialPage]);

  return (
    <UploadsWrapper>
      <UploadsHeader onClickUploadPDF={onUploadButtonClick} />
      <div>
        <UploadsTable
          uploads={pdfs}
          sortBy={sortBy}
          onClickSort={onClickSort}
          onViewIconClick={onViewIconClick}
          onDownloadIconClick={onDownloadIconClick}
          onDeleteIconClick={onDeleteIconClick}
        />
        {pdfs?.meta?.total > 15 && (
          <DashboardPagination initialPage={initialPage} pageCount={pageCount} onPageChange={onPageChange} />
        )}

        <UploadFileModal isOpen={isUploadModalOpen} setIsOpen={setIsUploadModalOpen} />
        <DeleteFileModal
          isOpen={isDeleteModalOpen}
          selectedPDF={selectedPDF}
          setIsOpen={setIsDeleteModalOpen}
          onDeleteSuccess={onDeleteSuccess}
        />

        {isViewModalOpen && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              zIndex: 100002,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={closeViewModal}
          >
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              style={{
                width: '90%',
                height: '90%',
                backgroundColor: '#fff',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderBottom: '1px solid #e8e7ed',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 16, color: '#333' }}>{viewPdfName}</span>
                <button
                  type="button"
                  onClick={closeViewModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 24,
                    cursor: 'pointer',
                    color: '#777',
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
              {viewPdfUrl ? (
                <object
                  data={viewPdfUrl}
                  type="application/pdf"
                  title={viewPdfName}
                  style={{ flex: 1, border: 'none', width: '100%' }}
                />
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Spinner loading />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </UploadsWrapper>
  );
}

const UploadsContainerMemo = memo(UploadsContainer, areEqual);

export { UploadsContainerMemo as UploadsContainer };
